import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles } from "lucide-react";

interface SignUpPromptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Display name of the gated feature (e.g. "Calendar"). */
  feature: string;
  /** Path the user was trying to reach; used as the post-auth redirect. */
  redirectTo?: string;
  /** Optional override copy. */
  description?: string;
}

/**
 * Friendly sign-up prompt shown when a guest tries to use an auth-only
 * feature (calendar, parent/tutor connect, dashboard, analysis, …).
 */
export default function SignUpPromptDialog({
  open,
  onOpenChange,
  feature,
  redirectTo,
  description,
}: SignUpPromptDialogProps) {
  const navigate = useNavigate();
  const goToAuth = (mode: "signup" | "signin") => {
    const params = new URLSearchParams();
    if (redirectTo) params.set("redirect", redirectTo);
    if (mode === "signup") params.set("mode", "signup");
    navigate(`/auth${params.toString() ? `?${params.toString()}` : ""}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">
            Sign up to use {feature}
          </DialogTitle>
          <DialogDescription className="text-center">
            {description ??
              `Create a free account to ${feature.toLowerCase() === "dashboard" ? "see your personal dashboard" : `use ${feature.toLowerCase()}`}, save your progress, and connect with parents or tutors. Practice activities stay free without an account.`}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col gap-2 sm:flex-col">
          <Button className="w-full" onClick={() => goToAuth("signup")}>
            Sign up free
          </Button>
          <Button
            variant="outline"
            className="w-full"
            onClick={() => goToAuth("signin")}
          >
            I already have an account
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => onOpenChange(false)}
          >
            Keep practicing as guest
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
